/**
 * Native product sources shared by every new PDP.
 * Agilite Media can deliberately override these sources; an unpinned export
 * cannot accidentally replace the Shopify gallery or product details.
 */
(function (global) {
  'use strict';

  function usableImages(list) {
    var seen = {};
    return (Array.isArray(list) ? list : []).filter(function (image) {
      if (!image || !image.src) return false;
      var key = String(image.src).replace(/^https?:/, '');
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function selectHeroImages(nativeImages, feedImages, feedPinned) {
    var nativeSet = usableImages(nativeImages);
    var feedSet = usableImages(feedImages);
    if (feedPinned && feedSet.length) return feedSet;
    if (nativeSet.length) return nativeSet;
    return feedSet;
  }

  function selectFeed(live, bundled) {
    function valid(feed) { return feed && Array.isArray(feed.items); }
    if (!valid(live)) return valid(bundled) ? bundled : null;
    if (!valid(bundled)) return live;
    // A reviewed preview export must not lose to an older bucket snapshot.
    // Empty items are valid editorial state, not permission to revive old media.
    var liveAt = Date.parse(live.generatedAt || '') || 0;
    var bundledAt = Date.parse(bundled.generatedAt || '') || 0;
    return bundledAt > liveAt ? bundled : live;
  }

  function collapseMetadata(list) {
    if (!list) return;
    Array.prototype.forEach.call(list.children, function (node) {
      // Never measure through our own display:none, and never hide a buy,
      // variant, terms, back-in-stock, delivery or asynchronously loaded block.
      node.classList.remove('agmf-blank-block');
      var type = node.getAttribute('data-block-type') || '';
      if (!/^(title|sku|price|rating)$/.test(type)) return;
      if (node.getBoundingClientRect().height < 1) node.classList.add('agmf-blank-block');
    });
  }

  function reconcileCardOnly(items, images, typesOff) {
    var fixed = {}, off = {};
    (images || []).forEach(function (image) {
      if (image.ck) fixed[image.ck] = true;
      if (image.itemId != null) fixed['id:' + image.itemId] = true;
    });
    (typesOff || []).forEach(function (type) { off[type] = true; });
    (items || []).forEach(function (item) {
      // The exporter may reserve media for its automatic gallery. If Shopify
      // supplies the actual gallery, that reservation is no longer a duplicate.
      // A type switched off in the feed must still stay off.
      if (item.cardOnly && !off[item.contentType] && !fixed[item.ck] && !fixed['id:' + item.id]) {
        item.cardOnly = false;
      }
    });
  }

  function targetId(button) {
    var direct = button.getAttribute('aria-controls') || button.getAttribute('data-target') || '';
    if (direct) return direct.replace(/^#/, '');
    var href = button.getAttribute('href') || '';
    if (href.charAt(0) === '#') return href.slice(1);
    var call = button.getAttribute('onclick') || '';
    var match = call.match(/['"]([^'"]+)['"]\s*\)/);
    return match ? match[1] : '';
  }

  function safePanelHtml(panel) {
    if (!panel) return '';
    var copy = panel.cloneNode ? panel.cloneNode(true) : null;
    if (!copy || !copy.querySelectorAll) return String(panel.innerHTML || '').trim();
    copy.querySelectorAll('script, style, template, form').forEach(function (node) { node.remove(); });
    copy.querySelectorAll('*').forEach(function (node) {
      Array.prototype.slice.call(node.attributes || []).forEach(function (attr) {
        var name = String(attr.name || '').toLowerCase();
        var value = String(attr.value || '');
        if (name.indexOf('on') === 0 || ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(value))) {
          node.removeAttribute(attr.name);
        }
      });
    });
    return String(copy.innerHTML || '').trim();
  }

  /* THE PRODUCT'S OWN METAFIELD WORDS (Dorian 2026-10-05: "the accordeons
     doesnt include what is written in the metafields ... in AM i do see it
     correctly but not in the actual pages").
     sections/agilite-media-flow.liquid renders custom.details (+ details_more),
     sizing (sizing_multiline wins), specs, returns and gov_sales - the fields
     Agilite Media edits and shows - into <script data-agmf-native-tabs>, in
     the page's language. They OWN their tabs. Before, nothing read them: the
     accordions came from the old pasted tab table in the description, whose
     buttons Shopify has stripped of their onclick on most products, so the
     page got one "Details" whose contents the spec CSS then hid.
     Description/Includes are the labels two products use for Details/Specs. */
  var TAB_ALIASES = { description: 'details', includes: 'specs' };

  function metafieldTabs(root) {
    var node = root.querySelector ? root.querySelector('script[data-agmf-native-tabs]') : null;
    var data = null;
    try { data = JSON.parse((node && node.textContent) || 'null'); } catch (e) { data = null; }
    var list = data && Array.isArray(data.tabs) ? data.tabs : [];
    var tabs = list.filter(function (t) {
      return t && t.key && String(t.html || '').trim();
    }).map(function (t) {
      var blocks = [{ type: 'html', html: String(t.html).trim() }];
      if (String(t.more || '').trim()) {
        blocks.push({ type: 'readmore', label: t.moreLabel || 'Read more' },
                    { type: 'html', html: String(t.more).trim() });
      }
      return { key: String(t.key).toLowerCase(), name: String(t.name || t.key), blocks: blocks };
    });
    // `primary` = the page is in the shop's primary language, so the old
    // table's button names are the English ones the keys are written in.
    return { tabs: tabs, primary: !data || data.primary !== false };
  }

  /* ONLY THE METAFIELDS, AND ALL OF THEM (Dorian 2026-10-06: "it should be the
     full metafield and only the metafields content"). When the product has its
     metafield words, they ARE the accordions - Details (with its Read more),
     Sizing, Specs, Returns, Gov Sales, in that order, each complete - and the
     old description block is folded away whole: no stray sentence, no FAQ tab
     from the pasted table, no empty line. The accordions take its place.
     The old table/description path below is only for a product with no
     metafield words at all. */
  function productTabs(root, handle) {
    if (!root || !root.querySelector) return null;
    var owned = metafieldTabs(root);
    if (owned.tabs.length) {
      var descBlocks = root.querySelectorAll
        ? Array.prototype.slice.call(root.querySelectorAll('.product-info [data-block-type="description"]'))
        : [];
      return {
        handle: handle || '',
        title: 'Product details',
        tabs: owned.tabs.map(function (t) { return { name: t.name, blocks: t.blocks }; }),
        native: true,
        anchor: descBlocks[0] || root.querySelector('.tab-customize'),
        originals: descBlocks
      };
    }
    var byKey = {};
    var used = {};
    owned.tabs.forEach(function (t) { byKey[t.key] = t; });
    function ownerOf(name) {
      var key = String(name || '').trim().toLowerCase();
      key = TAB_ALIASES[key] || key;
      return byKey[key] && !used[key] ? key : '';
    }
    function ownedTab(key) {
      used[key] = true;
      return { name: byKey[key].name, blocks: byKey[key].blocks };
    }
    function unusedOwned() {
      return owned.tabs.filter(function (t) { return !used[t.key]; }).map(function (t) { return ownedTab(t.key); });
    }
    var bar = root.querySelector('.tab-customize');
    var tabs = [];
    var originals = [];
    if (bar && bar.querySelectorAll) {
      // A button that names no target owns the content div at its own
      // position: the tables whose onclick Shopify stripped are still laid out
      // button N -> div N under the same parent.
      var scope = bar.parentNode && bar.parentNode.querySelectorAll ? bar.parentNode : null;
      var ordered = scope ? scope.querySelectorAll('.tabcontent-cus') : [];
      Array.prototype.forEach.call(bar.querySelectorAll('.tablinks-cus, button, [role="tab"]'), function (button, i) {
        var name = String(button.textContent || '').trim();
        if (!name) return;
        var key = ownerOf(name);
        if (key) { tabs.push(ownedTab(key)); return; }
        // On a translated page the table's names are not the English keys, so
        // its tabs would sit beside the metafield ones in two languages: there
        // the metafields alone make the accordions.
        if (owned.tabs.length && !owned.primary) return;
        var id = targetId(button);
        var panel = id ? (root.getElementById ? root.getElementById(id) : null) : (ordered[i] || null);
        var html = safePanelHtml(panel);
        if (!html) return;
        tabs.push({ name: name, blocks: [{ type: 'html', html: html }] });
      });
      tabs = tabs.concat(unusedOwned());
    }
    if (!tabs.length && root.querySelectorAll) {
      var blocks = root.querySelectorAll('.product-info [data-block-type="description"], .product-info [data-block-type="accordion"]');
      Array.prototype.forEach.call(blocks, function (block) {
        var summary = block.querySelector('summary');
        var body = block.querySelector('.accordion__content, .prose') || (!summary ? block : null);
        var name = summary ? String(summary.textContent || '').trim() : 'Details';
        var html = safePanelHtml(body);
        if (!name || !html) return;
        var key = ownerOf(name);
        tabs.push(key ? ownedTab(key) : { name: name, blocks: [{ type: 'html', html: html }] });
        originals.push(block);
      });
      tabs = tabs.concat(unusedOwned());
      if (originals.length) bar = originals[0];
    }
    if (!tabs.length) return null;
    return {
      handle: handle || '',
      title: 'Product details',
      tabs: tabs,
      native: true,
      anchor: bar,
      originals: originals
    };
  }

  global.AGMF_PRODUCT_SOURCES = {
    selectHeroImages: selectHeroImages,
    selectFeed: selectFeed,
    collapseMetadata: collapseMetadata,
    reconcileCardOnly: reconcileCardOnly,
    productTabs: productTabs
  };
}(window));
